import {
    headingsPlugin,
    MDXEditor,
    MDXEditorMethods,
    tablePlugin,
    linkPlugin,
    listsPlugin,
    toolbarPlugin,
    UndoRedo,
    BoldItalicUnderlineToggles,
    BlockTypeSelect,
    CreateLink,
    linkDialogPlugin,
    InsertImage,
    imagePlugin,
    InsertTable,
    InsertThematicBreak,
    thematicBreakPlugin,
    ListsToggle,
    Separator,
    DiffSourceToggleWrapper,
    diffSourcePlugin,
  } from "@mdxeditor/editor";
  import "@mdxeditor/editor/style.css";
  import './style.css'

  import React from "react";

  interface IMarkdownEditorProps {
    onChange: (val: string) => void;
    text: string;
  }

  // TOOLBAR_CONTENTS is pure JSX with no per-instance state — safe at module level.
  const TOOLBAR_CONTENTS = () => (
    <>
      {" "}
      <UndoRedo />
      <BoldItalicUnderlineToggles />
      <BlockTypeSelect />
      <CreateLink />
      <InsertImage />
      <InsertTable />
      <InsertThematicBreak />
      <ListsToggle />
      <Separator />
      <DiffSourceToggleWrapper>
        <UndoRedo />
      </DiffSourceToggleWrapper>
    </>
  );

  const MarkdownEditor = ({ text, onChange }: IMarkdownEditorProps) => {
    const ref = React.useRef<MDXEditorMethods>(null);

    // useMemo with [] gives per-instance scope: plugins are created once on mount,
    // not on every render, and not shared as module-level singletons.
    // MDXEditor plugins carry internal realm state (reactive atoms); sharing
    // instances across remounts can cause loops in stateful plugins like imagePlugin.
    const plugins = React.useMemo(() => [
      headingsPlugin(),
      tablePlugin(),
      linkPlugin(),
      listsPlugin(),
      linkDialogPlugin(),
      imagePlugin({ disableImageResize: true }),
      thematicBreakPlugin(),
      diffSourcePlugin(),
      toolbarPlugin({ toolbarContents: TOOLBAR_CONTENTS }),
    ], []);

    // Sync external text changes (e.g. band switch / form reset) via the ref
    // rather than via the `markdown` prop. Passing a new `markdown` prop
    // reinitializes the editor; setMarkdown() updates content in-place.
    const prevTextRef = React.useRef(text);
    React.useEffect(() => {
      if (ref.current && text !== prevTextRef.current) {
        ref.current.setMarkdown(text);
        prevTextRef.current = text;
      }
    }, [text]);

    return (
      <div>
        <MDXEditor
          ref={ref}
          onChange={onChange}
          markdown={text}
          plugins={plugins}
        />
      </div>
    );
  };

  // Custom equality: ignore `text` prop changes after mount — the editor
  // manages its own internal state and calls onChange when the user types.
  // Re-render only when `onChange` identity changes (e.g. a new band loads,
  // which is handled via the `key` prop in the parent).
  export default React.memo(MarkdownEditor, (prev, next) =>
    prev.onChange === next.onChange
  );
